/* eslint-disable */
/**
 * Cycle de vie d'une commande en salle.
 *
 *     ouverte → en_service → attente → caisse | moi
 *
 * `ouverte` est l'état de travail AVANT tout envoi : la table vient de
 * s'installer, le serveur compose la première fournée mais rien n'est encore
 * parti en cuisine.
 *
 * `en_service` (ajouté) est le vrai état du repas : au moins une fournée a été
 * « passée » (envoyée en cuisine via le QR de la caisse), et la table continue
 * de commander. C'est ici que se joue la nouveauté du module - on ne repasse
 * QUE les articles neufs, jamais toute la commande. Voir qteEnvoyee ci-dessous.
 *
 * `attente` signifie « QR de PAIEMENT généré, pas encore réglé » - la commande
 * n'est donc PAS encaissée. C'est la distinction que le point de caisse doit
 * préserver : ouverte, en_service et attente comptent dans le « reste à
 * encaisser », jamais dans le collecté.
 *
 * On a volontairement conservé le nom `attente` : des commandes portant déjà ce
 * statut existent en production, et le renommer imposerait une migration.
 *
 * Ce fichier n'est pas un mock : il décrit le domaine et survivra à la
 * disparition de mocks.js.
 */
export const STATUTS = {
    ouverte:      { libelle: "Ouverte",             ton: "ouverte" },
    en_service:   { libelle: "En service",          ton: "service" },
    attente:      { libelle: "En attente de scan",  ton: "attente" },
    verification: { libelle: "Vérification…",       ton: "verif" },
    caisse:       { libelle: "Encaissée en caisse", ton: "ok" },
    moi:          { libelle: "Encaissée par moi",   ton: "moi" },
};

/**
 * Une commande non encore réglée : elle occupe la table et reste due.
 *
 * `verification` en fait partie : la caisse a peut-être encaissé, mais tant que
 * le poll ne l'a pas CONFIRMÉ, on ne compte pas la commande comme réglée - sinon
 * un « payé » supposé à tort fausserait le point de caisse.
 */
export function estEnCours(statut) {
    return statut === "ouverte" || statut === "en_service"
        || statut === "attente" || statut === "verification";
}

/**
 * Une commande RÉGLÉE ne se modifie plus.
 *
 * `caisse` (encaissée à la caisse) et `moi` (encaissée par le serveur) sont des
 * états terminaux : la vente est comptabilisée. La rouvrir côté serveur pour
 * changer les lignes désynchroniserait la salle de ce qui a réellement été
 * facturé. On la garde donc en lecture seule - consultable, jamais éditable.
 */
export function estVerrouillee(statut) {
    return statut === "caisse" || statut === "moi";
}

/**
 * Libellé du client d'une commande.
 *
 * Une même table physique peut porter plusieurs clients indépendants (manque de
 * place) : chaque commande porte alors `client = { num, nom }`. Le num est
 * auto-incrémenté par table ; le nom, optionnel, permet de renommer (« Dame en
 * rouge »). Sans client (ex. mode sans table), la chaîne est vide.
 */
export function labelClient(commande) {
    const c = commande && commande.client;
    if (!c) return "";
    const nom = String(c.nom || "").trim();
    return nom || ("Client " + (Number(c.num) || 1));
}

/* ────────────────────────────────────────────────────────────────────────
   Suivi ligne par ligne de ce qui est déjà parti en cuisine.

   Chaque ligne porte `qte` (quantité voulue par la table, ici et maintenant)
   et `qteEnvoyee` (quantité déjà passée en cuisine). Tout en découle :

     - qte > qteEnvoyee  → il y a du NEUF à passer (+N).
     - qte < qteEnvoyee  → la table s'est ravisée : ANNULATION de plats déjà
                            partis (-N). Tracé, pas supprimé silencieusement.
     - qte = qteEnvoyee  → cette ligne est à jour côté cuisine.

   Le delta est SIGNÉ : c'est ce qui permet de transmettre aussi bien les
   ajouts que les annulations dans un même « Passer la commande ».
   ──────────────────────────────────────────────────────────────────────── */

/** Écart signé entre le voulu et le déjà-envoyé. >0 : à ajouter. <0 : à annuler. */
export function deltaLigne(l) {
    return (Number(l.qte) || 0) - (Number(l.qteEnvoyee) || 0);
}

/**
 * Identifiant stable d'une commande, tel que porté par le QR et le message
 * WebSocket : pid serveur + clé de commande. Clé de corrélation côté caisse.
 */
export function identifiantCommande(commande) {
    const pid = commande && commande.collaborateur ? commande.collaborateur.pid : "";
    return String(pid) + "-" + String((commande && commande.id) || "");
}

/* ────────────────────────────────────────────────────────────────────────
   Ingrédients retirés ou doublés (fiche technique).

   Une ligne porte `ingredients: [{ c, name, etat }]`, où `c` est le code court
   de l'ingrédient (fin de son pid, fourni par le backend) et `etat` vaut
   "retire" ou "double". Seul le code voyage vers la caisse, qui retrouve
   l'ingrédient dans sa propre liste : un pid complet fait plus de 90
   caractères et le QR est déjà dense.
   ──────────────────────────────────────────────────────────────────────── */

/** Code transporté par le QR : « rxxxxxx,dyyyyyy » (r = sans, d = double), trié. */
export function codeIngredients(l) {
    return ((l && l.ingredients) || [])
        .filter((i) => i && i.c && (i.etat === "retire" || i.etat === "double"))
        .map((i) => (i.etat === "retire" ? "r" : "d") + i.c)
        .sort()
        .join(",");
}

/** Texte lisible par le serveur : « sans oignon, double fromage ». */
export function texteIngredients(l) {
    return ((l && l.ingredients) || [])
        .filter((i) => i && (i.etat === "retire" || i.etat === "double"))
        .map((i) => (i.etat === "retire" ? "sans " : "double ") + i.name)
        .join(", ");
}

/**
 * Charge utile de la commande (payload v2, k:"cmd"), IDENTIQUE au QR et au
 * message WebSocket - d'où son extraction ici, pour que les deux transports ne
 * divergent jamais. Ni prix ni libellé : la caisse recalcule depuis son catalogue.
 * @param {object} commande
 * @param {"service"|"paiement"} mode
 * @returns {string} JSON prêt à encoder en QR ou à émettre sur le socket.
 */
export function payloadCommande(commande, mode) {
    const estService = mode === "service";
    const srv = (commande && commande.collaborateur) || {};
    return JSON.stringify({
        v: 2,
        k: "cmd",
        m: estService ? "svc" : "pay",
        id: identifiantCommande(commande),
        s: srv.pid || "",
        t: commande && commande.table ? commande.table.pid : "",
        cl: labelClient(commande),
        n: commande ? commande.numero : "",
        l: (commande && commande.lignes || []).map((l) => {
            const ligne = [
                l.pid, l.tarifPid, Number(l.qte) || 0, Number(l.qteEnvoyee) || 0,
                l.accompagnement || "", l.note || "",
            ];
            // 7e élément seulement s'il y a un choix : la plupart des lignes n'en
            // ont pas, autant de caractères gagnés sur le QR.
            const ing = codeIngredients(l);
            if (ing) ligne.push(ing);
            return ligne;
        }),
    });
}

/* ────────────────────────────────────────────────────────────────────────
   Plusieurs QR pour une commande.

   Au-delà d'une certaine taille, un QR devient trop fin pour être lu sur un
   écran de téléphone. La commande est alors découpée en plusieurs QR que le
   caissier lit à la suite, dans l'ordre qu'il veut ; la caisse les regroupe
   avant d'afficher l'aperçu.

   Chaque partie porte `p: [rang, total, tampon]`. Le tampon est une empreinte
   du contenu complet : il change dès que la commande change, ce qui empêche la
   caisse de mélanger les parties de deux versions. Seule la partie 1 porte
   l'en-tête (serveur, table, numéro) : il pèse près de 400 caractères, le
   répéter remplirait chaque QR à moitié.

   Le WebSocket n'est pas concerné : il envoie toujours le texte entier.
   ──────────────────────────────────────────────────────────────────────── */

/** Taille au-delà de laquelle la commande est découpée (caractères par QR). */
export const SEUIL_QR = 700;

/** Empreinte courte et stable d'un texte (FNV-1a 32 bits, en base 36). */
function empreinte(txt) {
    let h = 0x811c9dc5;
    for (let i = 0; i < txt.length; i++) {
        h ^= txt.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0).toString(36);
}

/**
 * Textes des QR d'une commande : un seul si elle tient sous le seuil, sinon
 * plusieurs parties numérotées.
 * @returns {string[]}
 */
export function payloadsQr(commande, mode, seuil) {
    const max = Number(seuil) > 0 ? Number(seuil) : SEUIL_QR;
    const entier = payloadCommande(commande, mode);
    if (entier.length <= max) return [entier];

    const o = JSON.parse(entier);
    const tampon = empreinte(entier);

    // Place prise par l'enveloppe d'une partie, lignes exclues. Le total est
    // compté sur deux chiffres : il n'est connu qu'une fois le découpage fini.
    const enveloppe = (rang) => (rang === 1
        ? JSON.stringify(Object.assign({}, o, { p: [1, 99, tampon], l: [] }))
        : JSON.stringify({ v: o.v, k: o.k, p: [rang, 99, tampon], l: [] })).length;

    const lots = [];
    let lot = [], taille = enveloppe(1);
    for (const ligne of o.l) {
        const poids = JSON.stringify(ligne).length + 1;
        // Une partie contient toujours au moins une ligne, même trop longue.
        if (lot.length && taille + poids > max) {
            lots.push(lot);
            lot = [];
            taille = enveloppe(lots.length + 1);
        }
        lot.push(ligne);
        taille += poids;
    }
    if (lot.length) lots.push(lot);
    if (lots.length < 2) return [entier];

    return lots.map((l, i) => (i === 0
        ? JSON.stringify(Object.assign({}, o, { p: [1, lots.length, tampon], l }))
        : JSON.stringify({ v: o.v, k: o.k, p: [i + 1, lots.length, tampon], l })));
}

/** Y a-t-il quoi que ce soit à repasser en cuisine (ajout OU annulation) ? */
export function aDelta(commande) {
    return !!(commande && (commande.lignes || []).some((l) => deltaLigne(l) !== 0));
}

/**
 * Qualifie une ligne pour l'affichage (badge du récapitulatif).
 *   nouveau  : jamais envoyée, entièrement à passer.
 *   ajout    : déjà partie, mais on en a rajouté.
 *   annule   : ramenée à zéro après envoi → à retirer en cuisine.
 *   retrait  : réduite sans disparaître (2 → 1) après envoi.
 *   envoye   : à jour, rien à faire.
 */
export function etatLigne(l) {
    const qte = Number(l.qte) || 0;
    const envoyee = Number(l.qteEnvoyee) || 0;
    if (qte === 0 && envoyee > 0) return "annule";
    if (envoyee === 0) return "nouveau";
    if (qte > envoyee) return "ajout";
    if (qte < envoyee) return "retrait";
    return "envoye";
}
